/**
 * categoryIconRegistry.js - Category chip styling registry
 *
 * Maps category leaf names to icons and tint families.
 * Uses normalized keys (lowercase, trimmed) for lookups.
 */

import {
  AlertCircle,
  ArrowLeftRight,
  Baby,
  Banknote,
  Bus,
  Car,
  Coffee,
  CreditCard,
  Film,
  Fuel,
  Gift,
  GraduationCap,
  Heart,
  HeartPulse,
  Home,
  Landmark,
  Laptop,
  Music,
  PiggyBank,
  Plane,
  Receipt,
  ShoppingBag,
  ShoppingCart,
  Smartphone,
  Sparkles,
  Stethoscope,
  Tag,
  Tv,
  Utensils,
  Wifi,
  Wrench,
  Zap,
  Building
} from 'lucide-react';

import logger from '../../utils/logger';
import {
  getCategoryParts,
  normalizeKey,
  isExcludedCategory,
  isUncategorizedCategory
} from './categoryChipUtils';

/**
 * Tint system for category chips
 * Each tint provides: background, border, text, and icon colors
 * Using low opacity for a subtle, premium glass-style feel
 *
 * Opacity guidelines:
 * - bg: ~8-12% opacity
 * - border: ~16-22% opacity
 * - text: ~85-92% (via Tailwind color scale)
 */
export const tintColors = {
  purple: { bg: 'vylo-tint-accent-bg', border: 'vylo-tint-accent-border', text: 'vylo-tint-accent-ink', icon: 'vylo-tint-accent-ink', lightBg: 'vylo-tint-accent-bg', lightBorder: 'vylo-tint-accent-border', lightText: 'vylo-tint-accent-ink', lightIcon: 'vylo-tint-accent-ink' },
  indigo: { bg: 'vylo-tint-accent-bg', border: 'vylo-tint-accent-border', text: 'vylo-tint-accent-ink', icon: 'vylo-tint-accent-ink', lightBg: 'vylo-tint-accent-bg', lightBorder: 'vylo-tint-accent-border', lightText: 'vylo-tint-accent-ink', lightIcon: 'vylo-tint-accent-ink' },
  blue: { bg: 'vylo-tint-info-bg', border: 'vylo-tint-info-border', text: 'vylo-tint-info-ink', icon: 'vylo-tint-info-ink', lightBg: 'vylo-tint-info-bg', lightBorder: 'vylo-tint-info-border', lightText: 'vylo-tint-info-ink', lightIcon: 'vylo-tint-info-ink' },
  teal: { bg: 'vylo-tint-teal-bg', border: 'vylo-tint-teal-border', text: 'vylo-tint-teal-ink', icon: 'vylo-tint-teal-ink', lightBg: 'vylo-tint-teal-bg', lightBorder: 'vylo-tint-teal-border', lightText: 'vylo-tint-teal-ink', lightIcon: 'vylo-tint-teal-ink' },
  green: { bg: 'vylo-tint-success-bg', border: 'vylo-tint-success-border', text: 'vylo-tint-success-ink', icon: 'vylo-tint-success-ink', lightBg: 'vylo-tint-success-bg', lightBorder: 'vylo-tint-success-border', lightText: 'vylo-tint-success-ink', lightIcon: 'vylo-tint-success-ink' },
  lime: { bg: 'vylo-tint-lime-bg', border: 'vylo-tint-lime-border', text: 'vylo-tint-lime-ink', icon: 'vylo-tint-lime-ink', lightBg: 'vylo-tint-lime-bg', lightBorder: 'vylo-tint-lime-border', lightText: 'vylo-tint-lime-ink', lightIcon: 'vylo-tint-lime-ink' },
  amber: { bg: 'vylo-tint-warning-bg', border: 'vylo-tint-warning-border', text: 'vylo-tint-warning-ink', icon: 'vylo-tint-warning-ink', lightBg: 'vylo-tint-warning-bg', lightBorder: 'vylo-tint-warning-border', lightText: 'vylo-tint-warning-ink', lightIcon: 'vylo-tint-warning-ink' },
  orange: { bg: 'vylo-tint-orange-bg', border: 'vylo-tint-orange-border', text: 'vylo-tint-orange-ink', icon: 'vylo-tint-orange-ink', lightBg: 'vylo-tint-orange-bg', lightBorder: 'vylo-tint-orange-border', lightText: 'vylo-tint-orange-ink', lightIcon: 'vylo-tint-orange-ink' },
  rose: { bg: 'vylo-tint-danger-bg', border: 'vylo-tint-danger-border', text: 'vylo-tint-danger-ink', icon: 'vylo-tint-danger-ink', lightBg: 'vylo-tint-danger-bg', lightBorder: 'vylo-tint-danger-border', lightText: 'vylo-tint-danger-ink', lightIcon: 'vylo-tint-danger-ink' },
  gray: { bg: 'vylo-tint-neutral-bg', border: 'vylo-tint-neutral-border', text: 'vylo-tint-neutral-ink', icon: 'vylo-tint-neutral-ink', lightBg: 'vylo-tint-neutral-bg', lightBorder: 'vylo-tint-neutral-border', lightText: 'vylo-tint-neutral-ink', lightIcon: 'vylo-tint-neutral-ink' },
};

/**
 * Category icon and tint registry
 * Keyed by normalized leaf category names (lowercase)
 *
 * Tint assignment philosophy:
 * - Housing + core fixed bills → blue/teal (stable)
 * - Food → green/lime
 * - Fun/lifestyle → rose/purple
 * - Transport → amber/orange
 * - Health → teal/green
 * - Education + debt + taxes → indigo/blue/gray (serious)
 * - Digital subscriptions → purple/indigo (varied by service)
 * - Travel → blue/indigo
 * - Uncategorized → gray (needs attention)
 * - Excluded → gray only (all same style)
 */
export const categoryRegistry = {
  // ============================================================================
  // FIXED - Housing & Core Bills (blue/teal - stable, trustworthy)
  // ============================================================================
  'rent/mortgage': { icon: Home, tint: 'blue' },
  'rent': { icon: Home, tint: 'blue' },
  'mortgage': { icon: Home, tint: 'blue' },
  'internet': { icon: Wifi, tint: 'teal' },
  'phone': { icon: Smartphone, tint: 'teal' },
  'electricity': { icon: Zap, tint: 'amber' },
  'heating': { icon: Zap, tint: 'amber' },
  'utilities': { icon: Zap, tint: 'amber' },
  'life insurance': { icon: Heart, tint: 'blue' },
  'rental insurance': { icon: Home, tint: 'blue' },
  'daycare': { icon: Baby, tint: 'rose' },

  // ============================================================================
  // FIXED - Transport (amber/orange - energy, movement)
  // ============================================================================
  'car payment': { icon: Car, tint: 'amber' },
  'car insurance': { icon: Car, tint: 'amber' },

  // ============================================================================
  // FIXED - Financial/Debt (indigo/gray - serious, formal)
  // ============================================================================
  'bank fees': { icon: Landmark, tint: 'gray' },
  'cc fees & interest': { icon: CreditCard, tint: 'gray' },
  'retirement savings fund': { icon: PiggyBank, tint: 'indigo' },
  'child education fund': { icon: GraduationCap, tint: 'indigo' },
  'line of credit': { icon: CreditCard, tint: 'indigo' },
  'education loan': { icon: GraduationCap, tint: 'indigo' },
  // Legacy names for backward compatibility
  'rrsp': { icon: PiggyBank, tint: 'indigo' },
  'resp': { icon: GraduationCap, tint: 'indigo' },

  // ============================================================================
  // FLEXIBLE - Food (green/lime - fresh, organic feel)
  // ============================================================================
  'groceries': { icon: ShoppingCart, tint: 'green' },
  'restaurants': { icon: Utensils, tint: 'lime' },
  'food delivery': { icon: Utensils, tint: 'green' },
  'coffee & snacks': { icon: Coffee, tint: 'lime' },

  // ============================================================================
  // FLEXIBLE - Fun/Lifestyle (rose/purple - playful, expressive)
  // ============================================================================
  'entertainment': { icon: Film, tint: 'rose' },
  'activities': { icon: Film, tint: 'rose' },
  'clothing': { icon: ShoppingBag, tint: 'rose' },
  'personal care': { icon: Sparkles, tint: 'rose' },
  'gifts': { icon: Gift, tint: 'purple' },
  'kids': { icon: Baby, tint: 'rose' },
  'kids & clothing': { icon: Baby, tint: 'rose' },

  // ============================================================================
  // FLEXIBLE - Transport (amber/orange - energy, movement)
  // ============================================================================
  'gas': { icon: Fuel, tint: 'amber' },
  'public transit': { icon: Bus, tint: 'amber' },
  'rideshare': { icon: Car, tint: 'orange' },
  'parking': { icon: Car, tint: 'amber' },

  // ============================================================================
  // FLEXIBLE - Health (teal/green - wellness, vitality)
  // ============================================================================
  'health': { icon: HeartPulse, tint: 'teal' },

  // ============================================================================
  // FLEXIBLE - Shopping/Home (teal - practical, grounded)
  // ============================================================================
  'shopping & retail': { icon: ShoppingBag, tint: 'teal' },
  'home & household': { icon: Home, tint: 'teal' },
  'shipping': { icon: Receipt, tint: 'teal' },
  'electronics': { icon: Laptop, tint: 'indigo' },

  // ============================================================================
  // NON-MONTHLY - Travel (blue/indigo - adventure, aspiration)
  // These use full category names including the hyphen
  // ============================================================================
  'travel - airfare': { icon: Plane, tint: 'blue' },
  'travel - lodging': { icon: Building, tint: 'indigo' },
  'travel - other': { icon: Plane, tint: 'blue' },

  // ============================================================================
  // NON-MONTHLY - Health (teal/green - wellness)
  // ============================================================================
  'medical': { icon: Stethoscope, tint: 'teal' },
  'dental': { icon: HeartPulse, tint: 'teal' },

  // ============================================================================
  // NON-MONTHLY - Home & Auto Repairs (amber - practical, hands-on)
  // ============================================================================
  'home repairs': { icon: Wrench, tint: 'amber' },
  'auto repairs': { icon: Wrench, tint: 'orange' },

  // ============================================================================
  // NON-MONTHLY - Education & Taxes (indigo/gray - serious)
  // ============================================================================
  'taxes': { icon: Receipt, tint: 'gray' },
  'education': { icon: GraduationCap, tint: 'indigo' },
  'donations': { icon: Heart, tint: 'rose' },

  // ============================================================================
  // DIGITAL SUBSCRIPTIONS - Streaming (purple/indigo - digital, modern)
  // ============================================================================
  'netflix': { icon: Tv, tint: 'rose' },
  'disney+': { icon: Tv, tint: 'blue' },
  'hulu': { icon: Tv, tint: 'green' },
  'amazon prime': { icon: Tv, tint: 'amber' },
  'youtube premium': { icon: Tv, tint: 'rose' },
  'streaming': { icon: Tv, tint: 'purple' },

  // ============================================================================
  // DIGITAL SUBSCRIPTIONS - Music (purple/indigo)
  // ============================================================================
  'spotify': { icon: Music, tint: 'green' },
  'apple music': { icon: Music, tint: 'rose' },
  'music streaming': { icon: Music, tint: 'purple' },

  // ============================================================================
  // DIGITAL SUBSCRIPTIONS - Software (indigo - professional)
  // ============================================================================
  'software': { icon: Laptop, tint: 'indigo' },

  // ============================================================================
  // EXCLUDED - All share neutral gray (de-emphasized, not counted)
  // Note: These are handled specially by resolveChipConfig, but included
  // here for icon lookup
  // ============================================================================
  'transfers': { icon: ArrowLeftRight, tint: 'gray' },
  'cc payments': { icon: CreditCard, tint: 'gray' },
  'refunds': { icon: ArrowLeftRight, tint: 'gray' },

  // ============================================================================
  // INCOME - Positive inflows
  // ============================================================================
  'salary': { icon: Banknote, tint: 'green' },
  'variable income': { icon: Banknote, tint: 'teal' },
  'other income': { icon: Banknote, tint: 'blue' },

  // ============================================================================
  // SPECIAL
  // ============================================================================
  'uncategorized': { icon: AlertCircle, tint: 'gray' }
};

/**
 * Default config for unknown categories
 */
export const defaultConfig = {
  icon: Tag,
  tint: 'gray',
  colors: tintColors.gray
};

/**
 * Track missing categories in development (warn only once per key)
 */
const warnedCategories = new Set();

/**
 * Resolve the complete chip configuration for a category
 * Handles parsing, normalization, excluded/uncategorized detection,
 * parent-aware overrides, fallback, and dev warnings.
 *
 * @param {string} rawCategory - The raw category string (e.g., "Flexible > Rideshare" or "Rideshare")
 * @param {string} [explicitParent] - Optional explicit parent category name
 * @returns {{ icon: Component, tint: string, colors: object, leaf: string }}
 */
export function resolveChipConfig(rawCategory, explicitParent = null) {
  // Parse the category string
  const { parent, leaf, raw } = getCategoryParts(rawCategory);

  // Use explicit parent if provided, otherwise use parsed parent
  const effectiveParent = explicitParent || parent;

  // Handle Uncategorized
  if (isUncategorizedCategory(leaf, raw)) {
    return {
      icon: AlertCircle,
      tint: 'gray',
      colors: tintColors.gray,
      leaf: 'Uncategorized'
    };
  }

  // Handle Excluded categories - always gray
  if (isExcludedCategory(leaf, effectiveParent)) {
    const normalizedLeaf = normalizeKey(leaf);
    const entry = categoryRegistry[normalizedLeaf];
    return {
      icon: entry?.icon || ArrowLeftRight,
      tint: 'gray',
      colors: tintColors.gray,
      leaf
    };
  }

  // Normal category lookup
  const normalizedLeaf = normalizeKey(leaf);
  const entry = categoryRegistry[normalizedLeaf];

  if (entry) {
    return {
      icon: entry.icon,
      tint: entry.tint,
      colors: tintColors[entry.tint],
      leaf
    };
  }

  // Fallback: category not found in registry
  // In development, warn about missing categories (once per key)
  if (process.env.NODE_ENV !== 'production') {
    if (!warnedCategories.has(normalizedLeaf)) {
      warnedCategories.add(normalizedLeaf);
      logger.warn('[CategoryChip] Missing registry entry for category', {
        hasLeaf: Boolean(leaf),
        normalizedLength: normalizedLeaf.length,
      });
    }
  }

  return {
    icon: defaultConfig.icon,
    tint: defaultConfig.tint,
    colors: defaultConfig.colors,
    leaf
  };
}

// ============================================================================
// LEGACY EXPORTS (for backward compatibility)
// ============================================================================

/**
 * Extract the leaf category from a full category string
 * @deprecated Use getCategoryParts from categoryChipUtils.js instead
 */
export function getLeafCategory(categoryString) {
  const { leaf } = getCategoryParts(categoryString);
  return leaf;
}

/**
 * Get icon and tint for a category
 * @deprecated Use resolveChipConfig instead
 */
export function getCategoryStyle(leafCategory, parentCategory = null) {
  // If leafCategory already contains parent info, use it directly
  const rawCategory = parentCategory
    ? `${parentCategory} > ${leafCategory}`
    : leafCategory;

  const config = resolveChipConfig(rawCategory);

  return {
    icon: config.icon,
    tint: config.tint,
    colors: config.colors
  };
}

// Re-export utilities for convenience
export { getCategoryParts, normalizeKey, isExcludedCategory, isUncategorizedCategory };
